import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useNavigate } from 'react-router-dom';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';
import { writeListFilters } from '../../../hooks/useSessionPersistedState';

const BULK_UPLOAD_CATEGORY_SESSION_KEYS = {
  CUSTOMER: 'sales/bulk-upload:selectedCategory',
  PRODUCT: 'sales/bulk-upload:selectedCategory',
  SUPPLIER: 'purchases/bulk-upload:selectedCategory',
};

export const createBulkUploadJob = async ({ formData }) => {
  const { data } = await apiClient.post('/bulk-uploads', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

export const useCreateBulkUploadHandler = (onCancel, redirectAfterSubmit) => {
  const dispatch = useDispatch();
  const { snackbarMessages } = useTranslatedMessages();
  const bulkUploadMessages = snackbarMessages.bulk_upload ?? {};
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();

  const mutation = useMutation({
    mutationFn: createBulkUploadJob,
    onSuccess: (_, variables) => {
      dispatch(showSnackbar({ message: bulkUploadMessages.upload_success, severity: 'success' }));
      queryClient.invalidateQueries({ queryKey: ['bulk_uploads'] });
      if (onCancel) onCancel();

      const uploadCategory = variables?.category;
      const categorySessionKey = BULK_UPLOAD_CATEGORY_SESSION_KEYS[uploadCategory];
      if (categorySessionKey) {
        writeListFilters(categorySessionKey, uploadCategory);
      }

      const destination = variables?.redirectAfterSubmit ?? redirectAfterSubmit;
      if (destination) navigate(destination);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 403) {
          showActionDeniedSnackbar();
          return;
        }
        dispatch(
          showSnackbar({
            message: error.response?.data?.message || bulkUploadMessages.upload_error,
            severity: 'error',
          }),
        );
      } else {
        dispatch(showSnackbar({ message: bulkUploadMessages.unexpected_error, severity: 'error' }));
      }
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const onSubmit = async (file, category, subCategory, selectedTemplate) => {
    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_name', selectedTemplate);
    formData.append('category', category);
    if (subCategory) {
      formData.append('subCategory', subCategory);
    }

    mutation.mutate({
      formData,
      category,
      redirectAfterSubmit,
    });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: mutation.error,
    isPending: mutation.isPending || loading,
    isSuccess: mutation.isSuccess,
    onSubmit,
  };
};
